import { Form } from "antd"
import { Flex, Spinner, Text, TextInput } from "components/common"
import { memo, useCallback, useMemo } from "react"
import { useSelector } from "react-redux"
import { t } from "i18next"
import { numberProps } from "features/leads/components/addSalesModal/utils/constants"
import { useAddSalesContext } from "features/leads/components/addSalesModal/addSalesContext/addSalesContext"
import { useGetDataFromImageMutation, useLazyGetAccountantsQuery } from "features/leads/api"
import PaymentProofImageUpload from "../../paymentProofImageUpload"
import SuggestedAmountTags from "./SuggestedAmountTags"
import RACheckbox from "./RACheckbox"
import BuyerFeeCheckbox from "./BuyerFeeCheckbox"
import AccountantSection from "./AccountantSection"
import ExtraFieldsSection from "./ExtraFieldsSection"
import PaymentDetailsCollapse from "./PaymentDetailsCollapse"
import { usePaymentProofUploadHandler } from "../hooks/usePaymentProofUploadHandler"
import { usePaymentRowEffects } from "../hooks/usePaymentRowEffects"
import {
  buildPaymentSuggestionItems,
  formatAmountSuffix,
  getPaymentSuffixMessage,
  getTotalRequiredPaymentAmount,
  hasPaymentProofValue,
  isPaymentProofUploading,
  PAYMENTS_FORM_LIST_NAME,
} from "../utils/paymentUtils"

const PaymentItem = memo(({ listFieldName, restField, rules }) => {
  const {
    form,
    addSalesApis,
    stateManager,
    formWatchers,
    minTokenDetails,
    isEscrowSelfFinance,
    lastPaymentStatus,
    lastPaymentType,
    vasEnabled,
    vasFees,
  } = useAddSalesContext()

  const { project, cdpCommitment } = formWatchers
  const { leadSalesInfo, getBankName, validateReference, getPaymentMode, paymentModesMap } = addSalesApis

  const [getDataFromImage, { isLoading }] = useGetDataFromImageMutation()
  const [getAccountant, { data: accountantData = [], isLoading: isLoadingAccountant }] = useLazyGetAccountantsQuery()

  const userPlatformKey = useSelector(state => state.app.user?.info?.AgencyConfiguration?.Platform?.platformKey)
  const projectId = project?.id || stateManager?.project?.projectId || leadSalesInfo?.projectId
  const isRaEnabled =
    project?.isRAFlowEnabled || stateManager?.project?.isRAFlowEnabled || leadSalesInfo?.isRAFlowEnabled
  const ocrEnabled =
    stateManager?.project?.paymentProof || project?.paymentProof || leadSalesInfo?.project?.paymentProof

  const fullPath = useMemo(() => [PAYMENTS_FORM_LIST_NAME, listFieldName], [listFieldName])

  const payments = Form.useWatch(PAYMENTS_FORM_LIST_NAME, form) || []
  const paymentProof = Form.useWatch([...fullPath, "paymentProof"], form)
  const modeOfPayment = Form.useWatch([...fullPath, "modeOfPayment"], form)
  const depositSlip = Form.useWatch([...fullPath, "bank_deposit_slip"], form)
  const enableRA = Form.useWatch([...fullPath, "enableRA"], form)
  const lineAmount = Form.useWatch([...fullPath, "amount"], form)
  const ocrData = Form.useWatch([...fullPath, "ocrData"], form)

  usePaymentRowEffects({
    fullPath,
    form,
    paymentProof,
    ocrData,
    modeOfPayment,
    paymentModesMap,
    validateReference,
    getBankName,
    getPaymentMode,
  })

  const handlePaymentProofUpload = usePaymentProofUploadHandler({
    fullPath,
    form,
    ocrEnabled,
    paymentModesMap,
    getDataFromImage,
  })

  const totalRequiredAmount = useMemo(
    () =>
      getTotalRequiredPaymentAmount({
        vasEnabled,
        leadSalesInfo,
        vasFees,
        minTokenDetails,
        dealPrice: stateManager?.dealPrice,
      }),
    [vasEnabled, leadSalesInfo, vasFees, minTokenDetails, stateManager?.dealPrice]
  )

  const suggestionParams = useMemo(
    () => ({
      project,
      leadSalesInfo,
      dealPrice: stateManager?.dealPrice,
      cdpCommitment,
      minTokenDetails,
      isEscrowSelfFinance,
      vasEnabled,
      lastPaymentStatus,
      lastPaymentType,
      vasFees,
      totalRequiredAmount,
    }),
    [
      project,
      leadSalesInfo,
      stateManager?.dealPrice,
      cdpCommitment,
      minTokenDetails,
      isEscrowSelfFinance,
      vasEnabled,
      lastPaymentStatus,
      lastPaymentType,
      vasFees,
      totalRequiredAmount,
    ]
  )

  const amountSuggestionItems = useMemo(() => {
    const previousTotal = payments?.slice(0, listFieldName).reduce((sum, item) => sum + (+item?.amount || 0), 0)
    return buildPaymentSuggestionItems({
      ...suggestionParams,
      rowIndex: listFieldName,
      previousPaymentsTotal: previousTotal,
      listFieldName,
    })
  }, [suggestionParams, payments, listFieldName])

  const suffixHandler = useCallback(
    lineAmountArg => {
      const message = getPaymentSuffixMessage({
        amount: lineAmountArg,
        dealPrice: stateManager?.dealPrice,
        vasEnabled,
        leadSalesInfo,
        vasFees,
      })
      return formatAmountSuffix(message, lineAmountArg, numberProps)
    },
    [stateManager?.dealPrice, leadSalesInfo, vasEnabled, vasFees]
  )

  const suffixText = useMemo(() => suffixHandler(+lineAmount || 0), [suffixHandler, lineAmount])

  const isRaFirst = isRaEnabled && !hasPaymentProofValue(paymentProof)

  return (
    <>
      {!!isRaFirst && (
        <RACheckbox {...restField} listFieldName={listFieldName} fieldName="enableRA" getAccountant={getAccountant} />
      )}

      <Form.Item
        {...(!isRaFirst ? restField : {})}
        preserve
        name={[listFieldName, "amount"]}
        label={t("Amount")}
        required
        extra={
          <SuggestedAmountTags
            suggestedAmounts={amountSuggestionItems}
            onAmountClick={item => {
              const value = item?.value != null ? item.value : null
              const path = [...fullPath, "amount"]
              form.setFieldValue(path, value)
              form.validateFields([path]).catch(() => {})
            }}
          />
        }
        rules={[{ required: true, message: t("Amount is required") }]}
        validateTrigger={["onChange", "onBlur"]}
      >
        <TextInput placeholder={t("Enter amount")} suffixText={suffixText} />
      </Form.Item>

      {!!enableRA && (
        <AccountantSection
          form={form}
          accountantData={accountantData}
          isLoadingAccountant={isLoadingAccountant}
          platformKey={userPlatformKey}
          projectId={projectId}
          listFieldName={listFieldName}
        />
      )}

      {!enableRA && (
        <Form.Item
          preserve
          name={[listFieldName, "paymentProof"]}
          label={t("Payment Proof")}
          required
          rules={[
            {
              validator: (_, value) =>
                hasPaymentProofValue(value) ? Promise.resolve() : Promise.reject(t("Payment Proof is Required")),
            },
          ]}
        >
          <PaymentProofImageUpload
            setFiles={handlePaymentProofUpload}
            paymentProofProps={{
              attachmentTypeId: 1,
              api: !!ocrEnabled,
              visionAllowedonProject: !!ocrEnabled,
              detectPaymentMode: !!ocrEnabled,
            }}
            platformKey={userPlatformKey}
            projectId={projectId}
          />
        </Form.Item>
      )}

      <BuyerFeeCheckbox listFieldName={listFieldName} />

      <ExtraFieldsSection
        paymentModesMap={paymentModesMap}
        form={form}
        getAccountant={getAccountant}
        accountantData={accountantData}
        isLoadingAccountant={isLoadingAccountant}
        platformKey={userPlatformKey}
        projectId={projectId}
        ocrEnabled={ocrEnabled}
        attachmentType={modeOfPayment || paymentProof?.imageData?.paymentModeKey}
        imageData={ocrData}
        isRaEnabled={isRaEnabled}
        listFieldName={listFieldName}
      />

      {!enableRA && hasPaymentProofValue(paymentProof) && (
        <PaymentDetailsCollapse
          ocrEnabled={ocrEnabled}
          isLoading={isLoading}
          paymentProof={paymentProof}
          depositSlip={depositSlip}
          listFieldName={listFieldName}
        />
      )}
    </>
  )
})

PaymentItem.displayName = "PaymentItem"

export default PaymentItem
